import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React from "react";
import { PatientTaskBroadcastDetails, PatientTaskInstanceId } from "../../../messages";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { Visit } from "../../../system/AppState/AppVisitsState";
import { List } from "../../../utils/List";
import TaskMapListedCard from "./TaskMapListedCard";
import VisitMapListedCard from "./VisitMapListedCard";
import VisitsOutsideAvailabilityTitle from "./VisitsOutsideAvailabilityTitle";

type Props = {
  caregiver: AppCaregiver;
  visits: List<Visit>;
  visitsOutsideAvailability: List<Visit>;
  tasks: List<PatientTaskBroadcastDetails>;
  onClickVisit: (visit: Visit) => void;
  onClickTask: (taskInstanceId: PatientTaskInstanceId) => void;
  onClickPreferenceSettings: () => void;
};

export default function VisitMapListedCards(props: Props) {
  const [initialTransition, setInitialTransition] = React.useState(true);

  React.useEffect(() => {
    setInitialTransition(false);
  }, []);

  return (
    <Root>
      {props.tasks.zipWithIndex().map(([task, idx]) => (
        <CardContainer
          key={task.taskInstanceId.toString()}
          idx={idx}
          initialTransition={initialTransition}
        >
          <TaskMapListedCard
            key={task.taskInstanceId.toString()}
            caregiver={props.caregiver}
            task={task}
            onClick={props.onClickTask}
          />
        </CardContainer>
      ))}

      {props.visits.zipWithIndex().map(([visit, idx]) => (
        <CardContainer
          key={visit.id.toString()}
          idx={idx + props.tasks.length()}
          initialTransition={initialTransition}
        >
          <VisitMapListedCard
            key={visit.id.toString()}
            caregiver={props.caregiver}
            visit={visit}
            onClick={props.onClickVisit}
          />
        </CardContainer>
      ))}

      {props.visitsOutsideAvailability.length() > 0 && (
        <>
          <CardContainer
            idx={props.tasks.length() + props.visits.length()}
            initialTransition={initialTransition}
          >
            <Divider />
            <VisitsOutsideAvailabilityTitle onClickSettings={props.onClickPreferenceSettings} />
          </CardContainer>

          {props.visitsOutsideAvailability.zipWithIndex().map(([visit, idx]) => (
            <CardContainer
              key={visit.id.toString()}
              idx={idx + props.tasks.length() + props.visits.length()}
              initialTransition={initialTransition}
            >
              <VisitMapListedCard
                key={visit.id.toString()}
                caregiver={props.caregiver}
                visit={visit}
                onClick={props.onClickVisit}
              />
            </CardContainer>
          ))}
        </>
      )}
    </Root>
  );
}

function CardContainer(props: {
  children: React.ReactNode;
  idx: number;
  initialTransition: boolean;
}) {
  return (
    <motion.div
      layout
      transition={{
        type: "spring",
        delay: props.initialTransition ? props.idx * 0.1 : 0,
        bounce: props.initialTransition ? 0.3 : 0,
      }}
      initial={{ scale: 0.7, opacity: 0, translateY: "100%" }}
      animate={{ scale: 1, opacity: 1, translateY: 0 }}
    >
      {props.children}
    </motion.div>
  );
}

const Root = styled.div`
  padding: 16px;

  > * + * {
    margin-top: 16px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--tw-slate-300);
  margin: 16px 0;
`;
